import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ChangePasswordService } from './change-password.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocalService } from '../../services/local.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, TranslateModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  providers: []
})
export class ChangePasswordComponent implements OnInit {
  userId: any = 0;
  changePasswordSubmitted = false;
  isUpdatePasswordButton: boolean = false;
  isShowAutoPassword: boolean = false;
  autoPassword: string = '';
  confirmFieldTextType: boolean = false;
  newFieldTextType: boolean = false;
  oldFieldTextType: boolean = false;
  changePasswordForm!: FormGroup;

  get formForPassword() { return this.changePasswordForm.controls; }

  constructor(private formBuilder: FormBuilder,
    private changePasswordService: ChangePasswordService,
    public toaster: ToastrService,
    private translateService: TranslateService,
    private localService: LocalService,
  ) { }

  ngOnInit(): void {
    this.changePasswordForm = this.formBuilder.group(
      {
        oldPassword: ['', Validators.required],
        newPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>[\]\/\\`~\-+=_])[A-Za-z\d!@#$%^&*(),.?":{}|<>[\]\/\\`~\-+=_]{8,}$/
            ),
          ],
        ],
        confirmPassword: ['', Validators.required],
        language: [localStorage.getItem('locale')],
      },
      {
        validators: [this.mustMatch('newPassword', 'confirmPassword')],
      } as AbstractControlOptions
    );

  }

  onPasswordSubmit(logout: boolean): void {

    const loginuserdata = this.localService.getJsonValue('loginUserData')??'[]';
    const userdata=JSON.parse(loginuserdata);

    this.isUpdatePasswordButton = true;
    const formData = this.changePasswordForm.value;
    formData.user_id = userdata.id;
    formData.rat = userdata.rw_auth_token; 
    formData.language = localStorage.getItem('locale');
    formData.logout_from_all_devices = logout ?? false;
    this.changePasswordService.updateAdminPassword(formData).subscribe((res: any) => {
      if (res.success) {
        this.toaster.success(res.message);
        this.changePasswordSubmitted = false;
        this.isShowAutoPassword = this.isUpdatePasswordButton = false;
        this.changePasswordForm.reset();
        this.markFormPristineAndUntouchedForPassword();
      } else {
        setTimeout(() => {
          this.isUpdatePasswordButton = false;
        }, 1000)
        this.toaster.error(res.message);

        // Show each error message in a separate toaster
        res.data.forEach((error: any) => this.toaster.error(error.message));
      }
    });
  }

  mustMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors['mustMatch']) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  markFormPristineAndUntouchedForPassword() {
    Object.keys(this.changePasswordForm.controls).forEach(key => {
      const control = this.changePasswordForm.get(key);
      if (control) {
        control.markAsPristine();
        control.markAsUntouched();
      }
    });
  }

  generatePassword() {
    const lowerChars = "abcdefghijklmnopqrstuvwxyz";
    const upperChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numberChars = "0123456789";
    const specialChars = "!@#$%^&*()_+";
    const allChars = lowerChars + upperChars + numberChars + specialChars;

    let password = "";
    password += lowerChars.charAt(Math.floor(Math.random() * lowerChars.length));
    password += upperChars.charAt(Math.floor(Math.random() * upperChars.length));
    password += numberChars.charAt(Math.floor(Math.random() * numberChars.length));
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));

    // Fill the remaining characters
    for (let i = 4; i < 11; i++) {
      password += allChars.charAt(Math.floor(Math.random() * allChars.length));
    }

    // Shuffle the password to avoid predictable pattern
    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    this.autoPassword = password;
    // return password;
  }

  toggleOldFieldTextType() {
    this.oldFieldTextType = !this.oldFieldTextType;
  }

  toggleConfirmFieldTextType() {
    this.confirmFieldTextType = !this.confirmFieldTextType;
  }

  toggleNewFieldTextType() {
    this.newFieldTextType = !this.newFieldTextType;
  }

  onCopyPassword(): void {
    const password = this.autoPassword;
    navigator.clipboard.writeText(password).then(() => {
      this.changePasswordForm.controls['newPassword'].setValue(password);
    });
  }

  passwordChange() {
    if (this.changePasswordForm.valid) {
      Swal.fire({
        // title: 'Are you sure?',
        text: this.translateService.instant('Would you like to logged out from all other devices?'),
        // icon: 'warning',
        width: '40rem',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.translateService.instant('Log me out from all devices'),
        cancelButtonText: this.translateService.instant('Keep me logged in'),
        allowOutsideClick: false
      }).then((result) => {
        if (result.isConfirmed) {
          this.onPasswordSubmit(true);
        } else {
          this.onPasswordSubmit(false);
        }
      });
    } else {
      this.changePasswordSubmitted = true;
    }
  }

}
